import React, { useState, useRef } from 'react';
import {
  Box, Button, VStack, HStack, Text, Alert, AlertIcon, AlertDescription,
  Progress, Badge, useToast, Icon, Divider,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  useColorModeValue, Stat, StatLabel, StatNumber, StatGroup,
  Avatar, Tooltip,
} from '@chakra-ui/react';
import { FiUpload, FiImage, FiCheck, FiX, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { uploadAvatar } from 'services/userService';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { updateCustomer } from 'services/woo';

// Extrae el RUT del nombre de archivo (sin extensión)
const rutFromFilename = (filename) => {
  return filename.replace(/\.[^/.]+$/, '').trim();
};

// Valida que sea una imagen y que el nombre parezca un RUT
const isValidImageFile = (file) => {
  return file.type.startsWith('image/');
};

const STATUS = { PENDING: 'pending', OK: 'ok', ERROR: 'error', SKIPPED: 'skipped' };

const AvatarImporter = () => {
  const [files, setFiles]       = useState([]); // [{ file, rut, status, message, previewUrl }]
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);
  const toast = useToast();

  const bgCard    = useColorModeValue('white', 'gray.800');
  const border    = useColorModeValue('gray.200', 'gray.600');
  const dropBg    = useColorModeValue('gray.50', 'gray.700');
  const dropActiveBg = useColorModeValue('blue.50', 'blue.900');
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  const stats = {
    total:   files.length,
    ok:      files.filter(f => f.status === STATUS.OK).length,
    error:   files.filter(f => f.status === STATUS.ERROR).length,
    pending: files.filter(f => f.status === STATUS.PENDING).length,
  };

  const handleFiles = (selected) => {
    const images = Array.from(selected).filter(isValidImageFile);
    if (images.length === 0) {
      toast({ title: 'Solo se aceptan imágenes (jpg, png, webp)', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    const items = images.map(file => ({
      file,
      rut: rutFromFilename(file.name),
      status: STATUS.PENDING,
      message: '',
      previewUrl: URL.createObjectURL(file),
    }));
    setFiles(items);
    setProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);

  const handleImport = async () => {
    if (files.length === 0) return;
    setImporting(true);
    setProgress(0);

    const updated = [...files];

    for (let i = 0; i < updated.length; i++) {
      const item = updated[i];
      try {
        // 1. Subir imagen a datofy-api
        const uploadResult = await uploadAvatar(item.rut, item.file);
        if (!uploadResult.success) {
          updated[i] = { ...item, status: STATUS.ERROR, message: uploadResult.message || 'Error al subir imagen' };
          setFiles([...updated]);
          setProgress(i + 1);
          continue;
        }

        // 2. Buscar el usuario por RUT (username) via endpoint unificado /datofy/v1/users
        const searchResults = await searchCustomers(item.rut, 10);
        const normalizedRut = item.rut.toLowerCase().trim();
        const customer = searchResults.find(
          c => c.username && c.username.toLowerCase().trim() === normalizedRut
        );
        if (!customer) {
          updated[i] = { ...item, status: STATUS.ERROR, message: 'Alumno no encontrado en el sistema' };
          setFiles([...updated]);
          setProgress(i + 1);
          continue;
        }

        // 3. Actualizar meta_data con la URL del avatar
        await updateCustomer(customer.id, {
          meta_data: [{ key: 'avatar_url', value: uploadResult.url }],
        });

        updated[i] = { ...item, status: STATUS.OK, message: uploadResult.url };
      } catch (e) {
        console.error('AvatarImporter error for', item.rut, e);
        updated[i] = { ...item, status: STATUS.ERROR, message: e.message || 'Error inesperado' };
      }
      setFiles([...updated]);
      setProgress(i + 1);
    }

    setImporting(false);
    const okCount = updated.filter(f => f.status === STATUS.OK).length;
    toast({
      title: `Importación completada: ${okCount} de ${updated.length} fotos actualizadas`,
      status: okCount > 0 ? 'success' : 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleReset = () => {
    files.forEach(f => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pct = files.length > 0 ? Math.round((progress / files.length) * 100) : 0;
  const isDone = !importing && progress === files.length && files.length > 0;

  return (
    <Box bg={bgCard} borderRadius="16px" border="1px solid" borderColor={border} p={6}>
      <VStack spacing={5} align="stretch">

        {/* ── Instrucciones ── */}
        <Alert status="info" borderRadius="10px" fontSize="sm">
          <AlertIcon as={FiInfo} />
          <AlertDescription>
            Selecciona imágenes cuyo <strong>nombre de archivo sea el RUT</strong> del alumno
            (ej: <code>12345678-9.jpg</code>). El sistema sube la foto a la carpeta del alumno
            en el sistema y actualiza su perfil.
          </AlertDescription>
        </Alert>

        {/* ── Zona de drop ── */}
        <Box
          border="2px dashed"
          borderColor={dragActive ? 'blue.400' : border}
          borderRadius="12px"
          bg={dragActive ? dropActiveBg : dropBg}
          p={8}
          textAlign="center"
          cursor="pointer"
          transition="all 0.2s"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Icon as={FiImage} boxSize={10} color={dragActive ? 'blue.400' : 'gray.400'} mb={3} />
          <Text fontWeight="semibold" color={dragActive ? 'blue.500' : mutedText}>
            {dragActive ? 'Suelta las imágenes aquí' : 'Arrastra imágenes o haz clic para seleccionar'}
          </Text>
          <Text fontSize="xs" color={mutedText} mt={1}>
            JPG, PNG, WEBP · El nombre del archivo debe ser el RUT (ej: 12345678-9.jpg)
          </Text>
        </Box>

        {/* ── Estadísticas ── */}
        {files.length > 0 && (
          <>
            <StatGroup textAlign="center">
              <Stat>
                <StatLabel fontSize="xs">Total</StatLabel>
                <StatNumber fontSize="xl">{stats.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="green.500">Actualizados</StatLabel>
                <StatNumber fontSize="xl" color="green.500">{stats.ok}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="orange.500">Pendientes</StatLabel>
                <StatNumber fontSize="xl" color="orange.500">{stats.pending}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="red.500">Errores</StatLabel>
                <StatNumber fontSize="xl" color="red.500">{stats.error}</StatNumber>
              </Stat>
            </StatGroup>

            {/* Barra de progreso */}
            {(importing || isDone) && (
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="xs" color={mutedText}>
                    {importing ? `Procesando ${progress} de ${files.length}…` : 'Completado'}
                  </Text>
                  <Text fontSize="xs" color={mutedText}>{pct}%</Text>
                </HStack>
                <Progress value={pct} colorScheme="blue" borderRadius="full" hasStripe={importing} isAnimated={importing} />
              </Box>
            )}

            {/* Tabla de archivos */}
            <TableContainer maxH="320px" overflowY="auto" borderRadius="8px" border="1px solid" borderColor={border}>
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg={bgCard} zIndex={1}>
                  <Tr>
                    <Th>Foto</Th>
                    <Th>Archivo</Th>
                    <Th>RUT detectado</Th>
                    <Th>Estado</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {files.map((item, idx) => (
                    <Tr key={idx}>
                      <Td>
                        <Avatar src={item.previewUrl} size="xs" name={item.rut} borderRadius="4px" />
                      </Td>
                      <Td fontSize="xs" maxW="160px" isTruncated>{item.file.name}</Td>
                      <Td fontSize="xs" fontFamily="mono">{item.rut}</Td>
                      <Td>
                        {item.status === STATUS.PENDING && (
                          <Badge colorScheme="gray" variant="subtle">Pendiente</Badge>
                        )}
                        {item.status === STATUS.OK && (
                          <Tooltip label={item.message}>
                            <Badge colorScheme="green" variant="subtle">
                              <HStack spacing={1}><Icon as={FiCheck} /><span>OK</span></HStack>
                            </Badge>
                          </Tooltip>
                        )}
                        {item.status === STATUS.ERROR && (
                          <Tooltip label={item.message}>
                            <Badge colorScheme="red" variant="subtle">
                              <HStack spacing={1}><Icon as={FiX} /><span>Error</span></HStack>
                            </Badge>
                          </Tooltip>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* ── Botones de acción ── */}
        <HStack justify="flex-end" spacing={3}>
          {files.length > 0 && (
            <Button variant="ghost" onClick={handleReset} isDisabled={importing} size="sm">
              Limpiar
            </Button>
          )}
          <Button
            colorScheme="blue"
            leftIcon={<Icon as={FiUpload} />}
            onClick={handleImport}
            isLoading={importing}
            loadingText={`Procesando ${progress}/${files.length}…`}
            isDisabled={files.length === 0 || importing || isDone}
            size="sm"
          >
            {isDone ? 'Importación completa' : `Importar ${files.length > 0 ? files.length + ' fotos' : ''}`}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default AvatarImporter;
